import React, { FC } from 'react';

import styled, { css } from 'styled-components/macro';

import { SpaceVariants } from 'theme';
import Icon from 'components/Icon';

const BTypes = {
    icon: css`
        min-width: 35px;
        width: 35px;
        height: 35px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: transparent;
        cursor: pointer;
        border-radius: 17.5px;
        padding: 0px;
        outline: none;
        transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;

        &:hover {
            background-color: rgb(235, 235, 235);
        }
    `,

    primary: css`
        color: ${({ theme }) => theme.colors.darkGrey};
        background-color: ${({ theme }) => theme.colors.brand};
    `,

    secondary: css`
        color: ${({ theme }) => theme.colors.body};
        border: 2px solid ${({ theme }) => theme.colors.body};

        &:hover,
        &.hover,
        &:focus,
        &.focus {
            background-color: ${({ theme }) => theme.colors.body};
            color: ${({ theme }) => theme.colors.white};
        }
    `,

    inactive: css`
        cursor: not-allowed;
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.labels};
    `,

    facebook: css`
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.facebook};
    `,

    bad: css`
        color: ${({ theme }) => theme.colors.white};
        background-color: ${({ theme }) => theme.colors.error};
    `,

    white: css`
        color: ${({ theme }) => theme.colors.darkGrey};
        background-color: ${({ theme }) => theme.colors.white};

        &:hover,
        &.hover,
        &:focus,
        &.focus {
            background-color: ${({ theme }) => theme.colors.darkGrey};
            color: ${({ theme }) => theme.colors.white};
        }
    `,

    transparent: css`
        color: ${({ theme }) => theme.colors.white};
        border: 2px solid ${({ theme }) => theme.colors.white};

        &:hover,
        &.hover,
        &:focus,
        &.focus {
            background-color: ${({ theme }) => theme.colors.white};
            color: ${({ theme }) => theme.colors.darkGrey};
        }
    `,
};

const MediumSize = css`
    padding: 0 ${({ theme }) => theme.space.medium};
    height: 32px;
    display: inline-block;
    font-size: 0.875rem;
`;

const Btn = styled.button<{
    $block: boolean;
    $medium: boolean;
    $buttonType:
        | 'icon'
        | 'primary'
        | 'secondary'
        | 'inactive'
        | 'facebook'
        | 'bad'
        | 'white'
        | 'transparent';
    $spaceTop?: SpaceVariants;
    $fontSize?: string;
    $fontWeight?: number;
}>`
    font-weight: 500;
    border-radius: 30px;
    padding: ${({ theme }) => `${theme.space.sbase} ${theme.space.large}`};

    text-align: center;
    outline: none;
    text-decoration: none;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
        border-color 0.15s ease-in-out;

    ${({ $block }) => $block && `display: block; width: 100%;`};
    ${({ $medium }) => $medium && MediumSize};
    ${({ $buttonType }) => BTypes[$buttonType]};
    ${({ disabled }) => disabled && BTypes['inactive']};
    ${({ $fontSize }) => $fontSize && `font-size: ${$fontSize};`}
    ${({ $fontWeight }) => $fontWeight && `font-weight: ${$fontWeight};`}

    ${({ theme, $spaceTop }) =>
        $spaceTop &&
        css`
            margin-top: ${theme.space[$spaceTop]};
        `};
`;

export type ButtonProps = {
    theme:
        | 'primary'
        | 'secondary'
        | 'white'
        | 'inactive'
        | 'bad'
        | 'facebook'
        | 'transparent';
    size?: 'large' | 'medium';
    disabled?: boolean;
    block?: boolean;
    icon?: string;
    iconSize?: number;
    to?: string;
    history?: any;
    spaceTop?: SpaceVariants;
    fontSize?: string;
    fontWeight?: number;

    /**
     * Actually data-attributes should be passed without typechecking.
     * But there is problem: we use ButtonProps in Actions component as its primary & secondary prop.
     *
     * Whenever data-* attributes are passed into Actions's primary or secondary prop,
     * TS complains about it. (e.g. src/components/ResubscribeOverview/index.tsx)
     *
     * So tmp solution was adding this data attribute here.
     * Any proposal on how to fix it are  welcome.
     */
    'data-type'?: string;
    'data-action'?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const Button: FC<ButtonProps> = ({
    theme = 'primary',
    size = 'large',
    block = false,
    disabled = false,
    iconSize = 18,
    spaceTop,
    fontSize,
    fontWeight,
    ...rest
}) => {
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
        if (rest.onClick && !disabled) rest.onClick(e);
        else if (rest.to) rest.history.push(rest.to);
    };

    const children = rest.icon ? (
        <Icon name={rest.icon} size={iconSize} />
    ) : (
        rest.children
    );

    return (
        <Btn
            {...rest}
            $block={block}
            $buttonType={rest.icon ? 'icon' : theme}
            $medium={size === 'medium'}
            $spaceTop={spaceTop}
            $fontSize={fontSize}
            $fontWeight={fontWeight}
            disabled={disabled || theme === 'inactive'}
            onClick={(e) => handleClick(e)}
        >
            {children}
        </Btn>
    );
};

export default Button;
